import React, { useEffect, useState } from 'react';
import { Anchor, Box, DataTable, Heading, Pagination } from 'grommet';
import { API, SearchSelect, Utils } from '@enrich/shared-lib';
import QueryString from 'qs';
import { useLoaderData } from 'react-router-dom';

const ACTION_COLORS = {
    resume_approved: 'enrich-grey',
    resume_edits_requested: 'enrich-green-light',
};

export const ResumeReviewerHistory = () => {
    const resumeReviewers = useLoaderData();
    const [currentReviewer, setCurrentReviewer] = useState();
    const [reviewerEvents, setReviewerEvents] = useState([]);

    const stepOptions = [25, 50, 100];
    const [page, setPage] = useState(1);
    const [totalItems, setTotalItems] = useState(0);
    const [pageSize, setPageSize] = useState(stepOptions[0]);
    const [sort, setSort] = useState();

    useEffect(() => {
        if (!page || !currentReviewer) return;
        let isActive = true;

        (async () => {
            const eventsResponse = await loadResumeApprovalEvents({
                currentReviewer,
                page,
                pageSize,
                sort,
            });

            if (isActive) {
                setPage(eventsResponse.meta.pagination.page);
                setReviewerEvents(eventsResponse.data);
                setTotalItems(eventsResponse.meta.pagination.total);
            }
        })();

        return () => {
            isActive = false;
        };
    }, [currentReviewer, page, pageSize, sort]);

    const handlePageChange = ({ page, startIndex, endIndex }) => {
        const newPageSize = endIndex - startIndex;
        if (pageSize != newPageSize) {
            setPageSize(newPageSize);
        }
        setPage(page);
    };

    const handleSort = ({ property, direction }) => {
        setSort({ property, direction });
        setPage(1);
    };

    const dataColumns = [
        {
            header: 'Contact Name',
            property: 'attributes.lastName',
            render: (event) => {
                return (
                    <Anchor
                        key={event.attributes.contact.data.id}
                        href={`/contacts/${event.attributes.contact.data.id}`}
                        label={`${event.attributes.contact.data.attributes.firstName} ${event.attributes.contact.data.attributes.lastName}`}
                    />
                );
            },
            sortable: true,
        },
        {
            header: 'Resume Action',
            property: 'attributes.type',
            render: (event) => {
                return (
                    <Box align="start">
                        <Box
                            round="small"
                            pad={{ horizontal: 'small', vertical: 'xsmall' }}
                            background={
                                ACTION_COLORS[event.attributes.type] ||
                                'enrich-blue'
                            }>
                            {Utils.convertEnumStringToLabel(
                                event.attributes.type,
                            )}
                        </Box>
                    </Box>
                );
            },
            sortable: true,
        },
        {
            header: 'Date',
            property: 'attributes.createdAt',
            render: (event) => {
                const approvalDate = new Date(event.attributes.createdAt);
                return approvalDate.toLocaleDateString();
            },
            sortable: true,
        },
    ];

    return (
        <Box pad={{ top: 'small' }}>
            <SearchSelect
                name="Resume Reviewer"
                labelKey={(user) => {
                    return `${user.firstName} ${user.lastName}`;
                }}
                valueKey="id"
                options={resumeReviewers}
                updateFormData={(user) => setCurrentReviewer(user)}
            />
            {currentReviewer && (
                <Box gap="medium">
                    <Box>
                        <Heading level="4">
                            {currentReviewer.firstName}{' '}
                            {currentReviewer.lastName}&apos;s Resume Approvals
                        </Heading>
                        <DataTable
                            primaryKey="id"
                            sort={sort}
                            onSort={handleSort}
                            border="bottom"
                            data={reviewerEvents}
                            columns={dataColumns}
                        />
                    </Box>
                    <Box align="center" justify="center" pad="small">
                        <Pagination
                            numberItems={totalItems}
                            onChange={({ page, startIndex, endIndex }) => {
                                handlePageChange({
                                    page,
                                    startIndex,
                                    endIndex,
                                });
                            }}
                            step={pageSize}
                            stepOptions={stepOptions}
                            summary={true}
                        />
                    </Box>
                </Box>
            )}
        </Box>
    );
};

const SORT_FIELD_MAP = {
    'attributes.lastName': 'contact.lastName',
    'attributes.type': 'type',
    'attributes.createdAt': 'createdAt',
};

const loadResumeApprovalEvents = async ({
    currentReviewer,
    page,
    pageSize,
    sort,
} = {}) => {
    const sortField = sort && SORT_FIELD_MAP[sort.property];
    const query = QueryString.stringify({
        filters: {
            type: {
                $in: ['resume_edits_requested', 'resume_approved'],
            },
            source_user: currentReviewer.id,
        },
        populate: ['contact'],
        pagination: page && pageSize ? { page, pageSize } : undefined,
        sort: sortField
            ? [`${sortField}:${sort.direction}`]
            : ['createdAt:desc'],
    });

    try {
        const response = await API.get(`/api/events?${query}`);
        return response.data;
    } catch (error) {
        console.log(error);
    }

    return [];
};

export async function loader() {
    const RESUME_REVIEWER_ROLE_ID = 4;
    const query = QueryString.stringify(
        {
            filters: { role: RESUME_REVIEWER_ROLE_ID },
        },
        { encodeValuesOnly: true },
    );

    try {
        const response = await API.get(`/api/users?${query}`);
        return response.data;
    } catch (error) {
        console.log(error);
    }

    return [];
}
